import React from "react";
import { useNavigate } from "react-router-dom";
import { formatDate, formatDateTime } from "../../utils/FormateDateTime";
import { extractTextFromHTML } from "../../utils/ExtractTextFromHtml";
import { useTranslation } from "react-i18next";

const EmrHistoryCardRow = ({ row, actions }) => {
    const navigate = useNavigate();
    const { t } = useTranslation();

    const clinicalItems = [
        { label: t('Chief Complaint'), value: extractTextFromHTML(row?.medical_assessments?.chief_complaints?.value) },
        { label: t('Diagnosis'), value: row?.diagnosisArr?.length > 0 ? row?.diagnosisArr.map((dg) => dg.codeDescription).join(", ") : null },
        { label: t('Significient Signs'), value: extractTextFromHTML(row?.medical_assessments?.significient_signs) },
        { label: t('Other Conditions'), value: extractTextFromHTML(row?.medical_assessments?.other_conditions) },
        { label: t('Past History'), value: extractTextFromHTML(row?.medical_assessments?.past_history) },
        { label: t('Progress Notes'), value: extractTextFromHTML(row?.doctor_notes?.progress_notes) },
        { label: t('Consult Notes'), value: extractTextFromHTML(row?.doctor_notes?.consult_notes) },
    ].filter(item => item.value && item.value !== "-------");

    const vitals = row?.vital_signs ? {
        systolic: row?.vital_signs?.find(dm => dm?.category === "vital-sign-systolic")?.value,
        diastolic: row?.vital_signs?.find(dm => dm?.category === "vital-sign-diastolic")?.value,
        pulse: row?.vital_signs?.find(dm => dm?.category === "vital-sign-pulse")?.value,
        temp: row?.vital_signs?.find(dm => dm?.category === "vital-sign-temperature")?.value,
    } : null;

    const hasVitals = vitals && (vitals.systolic || vitals.pulse || vitals.temp);
    const hasPregnancy = row?.patient?.gender === "female" && row?.medical_case_attribute?.pregnancy;

    return (
        <div className="mb-4 border border-gray-200 rounded-lg overflow-hidden transition-all hover:border-slate-300 group bg-white shadow-sm">
            <div className="bg-gray-100 px-4 py-2.5 border-b border-gray-200 flex flex-wrap items-center justify-between gap-4">
                <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                    <div className="flex flex-col">
                        <span className="text-[10px] font-medium text-slate-500 uppercase  leading-none mb-1">
                            {t(' Date')}
                        </span>
                        <span className="text-[14px] font-medium text-slate-900 tabular-nums">
                            {formatDateTime(row?.created_at)}
                        </span>
                    </div>
                    <div className="flex flex-col border-l border-slate-200 pl-4">
                        <span className="text-[10px] font-medium text-slate-500 uppercase  leading-none mb-1">
                            {t('Practitioner')}
                        </span>
                        <span className="text-[14px] font-medium text-slate-700">
                            {row?.careTeamsArr?.[0]?.practitionerName || row?.practitioner?.name || "-------"}
                        </span>
                    </div>
                    <div className="hidden sm:flex flex-col border-l border-slate-200 pl-4 text-left">
                        <span className="text-[10px] font-medium text-slate-500 uppercase  leading-none mb-1">
                            {t('Dept')}
                        </span>
                        <span className="text-[14px] font-medium text-slate-700">
                            {row?.practitioner?.department?.name_en || "-------"}
                        </span>
                    </div>
                </div>

                <div className="flex items-center gap-1">
                    {actions(row, navigate)}
                </div>
            </div>

            {(hasVitals || hasPregnancy) && (
                <div className="flex flex-wrap items-center gap-x-8 gap-y-2 px-4 py-2 bg-slate-50/40 border-b border-gray-100/60">
                    {hasVitals && (
                        <div className="flex gap-6">
                            {vitals.systolic && (
                                <div className="flex gap-2 items-center">
                                    <span className="text-slate-500 text-[10px] uppercase font-medium ">{t('BP')}</span>
                                    <span className="text-[13px] font-medium text-slate-600 tabular-nums">{vitals.systolic}/{vitals.diastolic} <span className="text-[9px] font-medium opacity-50 uppercase">mmhg</span></span>
                                </div>
                            )}
                            {vitals.pulse && (
                                <div className="flex gap-2 items-center">
                                    <span className="text-slate-500 text-[10px] uppercase font-medium ">{t('HR')}</span>
                                    <span className="text-[13px] font-medium text-slate-600 tabular-nums">{vitals.pulse} <span className="text-[9px] font-medium opacity-50 uppercase">bpm</span></span>
                                </div>
                            )}
                            {vitals.temp && (
                                <div className="flex gap-2 items-center">
                                    <span className="text-slate-500 text-[10px] uppercase font-medium ">{t('Temp')}</span>
                                    <span className="text-[13px] font-medium text-slate-600 tabular-nums">{vitals.temp}°C</span>
                                </div>
                            )}
                        </div>
                    )}
                    {hasPregnancy && (
                        <div className="flex items-center gap-3 ml-auto text-[10px] font-medium text-pink-500 uppercase tracking-tight">
                            <span>LMP {row?.lmp ? formatDate(row?.lmp) : "--"}</span>
                            <span className="w-1 h-3 bg-pink-100 rounded-full"></span>
                            <span>EDD {row?.edd ? formatDate(row?.edd) : "--"}</span>
                        </div>
                    )}
                </div>
            )}

            <div className="p-4 bg-white">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-5">
                    {clinicalItems.map((item, i) => (
                        <div key={i} className="flex flex-col gap-1.5">
                            <span className="text-[10px] font-medium text-slate-500 uppercase  leading-none">
                                {item.label}
                            </span>
                            <p className="text-[13.5px] text-slate-600 font-medium leading-relaxed break-words overflow-hidden">
                                {item.value}
                            </p>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default EmrHistoryCardRow;
